import { CommonModule } from "@angular/common";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { UiToggleModule } from "@quantive/ui-kit/toggle";
import { AssigneesModule } from "@webapp/assignees/assignees.module";
import { CommentsModule } from "@webapp/comments";
import { FeatureTogglesModule } from "@webapp/feature-toggles/feature-toggles.module";
import { LocalizationModule } from "@webapp/localization/localization.module";
import { GifViewModule } from "@webapp/shared/components/gif-view/gif-view.module";
import { NothingFoundModule } from "@webapp/shared/components/nothing-found/nothing-found.module";
import { ErrorNotificationModule } from "@webapp/shared/error-notification/error-notification.module";
import { FeatureBannerModule } from "@webapp/shared/feature-banner/feature-banner.module";
import { IndicatorModule } from "@webapp/shared/indicator/indicator.module";
import { CutModule } from "@webapp/shared/pipes/cut/cut.module";
import { TimeAgoPipe } from "@webapp/shared/pipes/time-ago/time-ago.pipe";
import { RichTextEditorModule } from "@webapp/shared/rich-text-editor/rich-text-editor.module";
import { UiAlertModule } from "@webapp/ui/alert/alert.module";
import { UiAvatarModule } from "@webapp/ui/avatar/avatar.module";
import { UiButtonModule } from "@webapp/ui/button/button.module";
import { UiCardModule } from "@webapp/ui/card/card.module";
import { UiCollapseModule } from "@webapp/ui/collapse/collapse.module";
import { UiDrawerModule } from "@webapp/ui/drawer/drawer.module";
import { UiGridModule } from "@webapp/ui/grid/grid.module";
import { UiIconModule } from "@webapp/ui/icon/icon.module";
import { UiInputModule } from "@webapp/ui/input/input.module";
import { UiLabelModule } from "@webapp/ui/label/label.module";
import { UiListModule } from "@webapp/ui/list/list.module";
import { UiPaginationModule } from "@webapp/ui/pagination/pagination.module";
import { UiSelectModule } from "@webapp/ui/select/select.module";
import { UiSkeletonModule } from "@webapp/ui/skeleton/skeleton.module";
import { UiTabModule } from "@webapp/ui/tab/tab.module";
import { UiTooltipModule } from "@webapp/ui/tooltip/tooltip.module";
import { CadenceCompareButtonComponent } from "./components/cadence-compare-button/cadence-compare-button.component";
import { CadenceLabelComponent } from "./components/cadence-label/cadence-label.component";
import { CadencePagerComponent } from "./components/cadence-pager/cadence-pager.component";
import { CheckinSkeletonComponent } from "./components/checkin-skeleton/checkin-skeleton.component";
import { CheckinsNewLabelComponent } from "./components/checkins-new-label/checkins-new-label.component";
import { ExpandCollapseButtonsComponent } from "./components/expand-collapse-buttons/expand-collapse-buttons.component";
import { ManageReflectionQuestionsComponent } from "./components/manage-reflections/manage-reflection-questions/manage-reflection-questions.component";
import { ManageReflectionScheduleComponent } from "./components/manage-reflections/manage-reflection-schedule/manage-reflection-schedule.component";
import { ManageReflectionsComponent } from "./components/manage-reflections/manage-reflections.component";
import { NoCheckInsComponent } from "./components/no-checkins/no-checkins.component";
import { ReflectionMoodComponent } from "./components/reflection-mood/reflection-mood.component";
import { ReflectionNoteComponent } from "./components/reflection-note/reflection-note.component";
import { CheckinActionButtonsComponent } from "./components/reflection-orchestrator/checkin-action-buttons/checkin-action-buttons.component";
import { CheckinCommentsCounterComponent } from "./components/reflection-orchestrator/checkin-comments-counter/checkin-comments-counter.component";
import { CheckinHideCommentsButtonComponent } from "./components/reflection-orchestrator/checkin-hide-comments-button/checkin-hide-comments-button.component";
import { CheckinLatestCommentDateComponent } from "./components/reflection-orchestrator/checkin-latest-comment-date/checkin-latest-comment-date.component";
import { CollapsePanelHeaderCompareComponent } from "./components/reflection-orchestrator/collapse-panel-header/collapse-panel-header-compare/collapse-panel-header-compare.component";
import { CollapsePanelHeaderDefaultComponent } from "./components/reflection-orchestrator/collapse-panel-header/collapse-panel-header-default/collapse-panel-header-default.component";
import { CollapsePanelHeaderDirective } from "./components/reflection-orchestrator/collapse-panel-header/collapse-panel-header.directive";
import { ReflectionCompareComponent } from "./components/reflection-orchestrator/reflection-compare/reflection-compare.component";
import { ReflectionLabelGroupComponent } from "./components/reflection-orchestrator/reflection-label-group/reflection-label-group.component";
import { ReflectionOrchestratorComponent } from "./components/reflection-orchestrator/reflection-orchestrator.component";
import { ReflectionOverviewComponent } from "./components/reflection-overview/reflection-overview.component";
import { ReflectionQuestionComponent } from "./components/reflection-question/reflection-question.component";
import { TeamReflectionsComponent } from "./components/team-reflections/team-reflections.component";
import { AutoFocusDirective } from "./directives/auto-focus.directive";
import { CadencePagerDatePipe } from "./pipes/cadence-pager-date.pipe";
import { CheckinsTargetTypePipe } from "./pipes/checkins-target-type.pipe";
import { LabelsFilterPipe } from "./pipes/labels.pipe";
import { NotesFilterPipe } from "./pipes/notes.pipe";

@NgModule({
  declarations: [
    TeamReflectionsComponent,
    ManageReflectionsComponent,
    NoCheckInsComponent,
    CadencePagerComponent,
    CadenceLabelComponent,
    CadencePagerDatePipe,
    ManageReflectionQuestionsComponent,
    ManageReflectionScheduleComponent,
    ReflectionQuestionComponent,
    ReflectionNoteComponent,
    ReflectionMoodComponent,
    ReflectionOverviewComponent,
    ReflectionOrchestratorComponent,
    ReflectionLabelGroupComponent,
    ExpandCollapseButtonsComponent,
    ReflectionCompareComponent,
    CadenceCompareButtonComponent,
    CheckinActionButtonsComponent,
    LabelsFilterPipe,
    NotesFilterPipe,
    AutoFocusDirective,
    CheckinSkeletonComponent,
    CheckinsNewLabelComponent,
    CheckinHideCommentsButtonComponent,
    CheckinCommentsCounterComponent,
    CheckinLatestCommentDateComponent,
    CollapsePanelHeaderDefaultComponent,
    CollapsePanelHeaderCompareComponent,
    CollapsePanelHeaderDirective,
    CheckinsTargetTypePipe,
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LocalizationModule,
    NothingFoundModule,
    RichTextEditorModule,
    CutModule,
    UiButtonModule,
    UiIconModule,
    UiGridModule,
    UiTabModule,
    UiListModule,
    UiTooltipModule,
    UiSelectModule,
    UiToggleModule,
    UiInputModule,
    UiPaginationModule,
    UiCollapseModule,
    UiLabelModule,
    UiAvatarModule,
    IndicatorModule,
    UiAlertModule,
    ErrorNotificationModule,
    UiCardModule,
    UiSkeletonModule,
    GifViewModule,
    FeatureTogglesModule,
    UiDrawerModule,
    CommentsModule,
    TimeAgoPipe,
    AssigneesModule,
    FeatureBannerModule,
  ],
})
export class ReflectionsModule {}
